// Shared helpers for admin portal pages. Every admin endpoint returns the
// {message, result} envelope confirmed against the live backend. Paginated
// list endpoints return result = {items, total, page, page_size, total_pages}.

import dayjs from "dayjs";

// Unwrap the {message, result} envelope. Raw axios data from apiService helpers
// lands here; caller gets the inner `result` (or the object itself if backend
// ever omits the wrapper on a given route).
export const unwrap = (raw) => {
  if (!raw || typeof raw !== "object") return raw;
  if ("result" in raw) return raw.result;
  return raw;
};

// Extract a paginated list response. Defensive about missing fields so the
// UI renders an empty table instead of throwing on unexpected shapes.
export const unwrapList = (raw) => {
  const result = unwrap(raw) || {};
  return {
    items: Array.isArray(result.items) ? result.items : [],
    total: Number(result.total) || 0,
    page: Number(result.page) || 1,
    page_size: Number(result.page_size) || 50,
    total_pages: Number(result.total_pages) || 0,
  };
};

// ISO 8601 → display. Empty / null / invalid dates render as "—" so tables
// don't show the browser's local representation of `null`.
export const formatDate = (iso, withTime = false) => {
  if (!iso) return "-";
  const d = dayjs(iso);
  if (!d.isValid()) return "-";
  return d.format(withTime ? "DD MMM YYYY HH:mm" : "DD MMM YYYY");
};

// INR currency formatter for decimals (backend emits as string). Handles
// null / "" / non-numeric input gracefully.
export const formatINR = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n);
};

// Compute the minimal diff between a baseline object and the form's current
// values. Used by Edit drawers so `PUT /admin/{...}` only sends changed fields
// (per prompt's partial-update rule — verify via network tab).
export const diffChanges = (baseline, current) => {
  const patch = {};
  Object.keys(current).forEach((key) => {
    const before = baseline?.[key];
    const after = current[key];

    // Treat "" and null as equal so clearing a never-set field isn't an edit.
    const normBefore = before === undefined || before === null ? "" : before;
    const normAfter = after === undefined || after === null ? "" : after;

    // dayjs objects → ISO for comparison
    const bVal = dayjs.isDayjs(normBefore) ? normBefore.toISOString() : normBefore;
    const aVal = dayjs.isDayjs(normAfter) ? normAfter.toISOString() : normAfter;

    if (bVal !== aVal) {
      patch[key] = dayjs.isDayjs(after) ? after.toISOString() : after;
    }
  });
  return patch;
};

// Map a backend validation error to a specific AntD Form field. The backend
// raises 400/409 with {detail: "email already taken"} or {detail: [{loc: [...],
// msg: "..."}]}. This returns an array shaped for AntD's form.setFields().
export const mapFieldErrors = (error, fieldGuesses = {}) => {
  const detail = error?.response?.data?.detail || error?.detail;
  const fields = [];

  if (typeof detail === "string") {
    // Heuristic mapping: if the message mentions a known field, target it.
    const msg = detail.toLowerCase();
    for (const [needle, targetField] of Object.entries(fieldGuesses)) {
      if (msg.includes(needle.toLowerCase())) {
        fields.push({ name: targetField, errors: [detail] });
        return fields;
      }
    }
  } else if (Array.isArray(detail)) {
    detail.forEach((d) => {
      const loc = d.loc || [];
      const fieldName = loc[loc.length - 1];
      if (fieldName) fields.push({ name: fieldName, errors: [d.msg] });
    });
  }
  return fields;
};

// ─── Breadcrumb filter-state preservation ────────────────────────────────────
//
// Admin/partner-admin list pages use ?queryString-driven filter state
// (partner_id, plan, suspended, search, etc. via useSearchParams). When a
// user clicks a row to drill into a detail page, the active filter set is
// stashed via `react-router`'s `state.fromQuery`. The detail page then
// renders its breadcrumb back-Link with `to={{pathname, search}}` so
// clicking it returns to the same filtered view.
//
// Shape: `state.fromQuery` is the raw `searchParams.toString()` string —
// e.g. `"partner_id=abc&suspended=true&page=3"`. Empty string when no
// filters are active; the breadcrumb Link still works.
//
// Why state, not sessionStorage / referrer parsing:
// - Survives back/forward correctly (each history entry has its own state)
// - No cross-tab leakage
// - No race when the user navigates between two list pages quickly

// Build the props bundle a list-page row Link needs. Pass the page's
// current `searchParams` (as the URLSearchParams instance) so the helper
// extracts the string verbatim.
export const linkStateFromQuery = (searchParams) => {
  if (!searchParams) return undefined;
  const search = searchParams.toString();
  if (!search) return undefined;
  return { fromQuery: search };
};

// Compute the `to` prop for a detail-page breadcrumb back-Link. Reads
// `location.state.fromQuery` (set by the list page) and folds it back
// into the pathname + search shape react-router expects.
export const breadcrumbBackTo = (pathname, locationState) => {
  const fromQuery =
    locationState && typeof locationState.fromQuery === "string"
      ? locationState.fromQuery
      : "";
  return fromQuery ? { pathname, search: `?${fromQuery}` } : pathname;
};
